import { useCallback } from 'react';

import {
  parseQuantityInputRaw,
  clampQuantity,
} from '@/shared/utils/quantityUtils';

import { CartItem } from '../types/CartTypes';

export const useQuantityValidation = () => {
  const validateQuantity = useCallback(
    (
      item: CartItem,
      newQuantity: number,
    ): {
      isValid: boolean;
      validQuantity: number;
      message?: string;
      shouldDelete?: boolean;
    } => {
      const minQuantity = item.productOption.minimumOrderQuantity || 1;
      const maxQuantity =
        item.productOption.stock - item.productOption.holdStock;

      const validQuantity = clampQuantity(
        newQuantity,
        maxQuantity,
        minQuantity,
      );

      if (validQuantity !== newQuantity) {
        if (newQuantity < minQuantity) {
          return {
            isValid: false,
            validQuantity,
            message: `Minimum order quantity is ${minQuantity}`,
          };
        }

        if (newQuantity > maxQuantity) {
          return {
            isValid: false,
            validQuantity,
            message: `Maximum available quantity is ${maxQuantity}`,
          };
        }
      }

      return {
        isValid: true,
        validQuantity,
      };
    },
    [],
  );

  const canIncreaseQuantity = useCallback((item: CartItem): boolean => {
    const maxQuantity = item.productOption.stock - item.productOption.holdStock;
    return item.quantity < maxQuantity;
  }, []);

  const canDecreaseQuantity = useCallback((item: CartItem): boolean => {
    const minQuantity = item.productOption.minimumOrderQuantity || 1;
    return item.quantity > minQuantity;
  }, []);

  const isQuantityBelowMOQ = useCallback((item: CartItem): boolean => {
    const minQuantity = item.productOption.minimumOrderQuantity || 1;
    return item.quantity < minQuantity;
  }, []);

  const getItemsBelowMOQ = useCallback(
    (cartItems: CartItem[]): CartItem[] => {
      return cartItems.filter(isQuantityBelowMOQ);
    },
    [isQuantityBelowMOQ],
  );

  const parseQuantityInput = useCallback(
    (
      item: CartItem,
      inputValue: string,
    ): {
      isValid: boolean;
      validQuantity: number;
      message?: string;
    } => {
      const minQuantity = item.productOption.minimumOrderQuantity || 1;
      const originalValue = parseQuantityInputRaw(inputValue, minQuantity);
      const validationResult = validateQuantity(item, originalValue);

      return validationResult;
    },
    [validateQuantity],
  );

  return {
    validateQuantity,
    parseQuantityInput,
    canIncreaseQuantity,
    canDecreaseQuantity,
    isQuantityBelowMOQ,
    getItemsBelowMOQ,
  };
};
